// The catch-all route.
//
// The game had none. Any address it did not recognise rendered the app shell
// and nothing else, so a recap email pointing at a route that had not shipped
// yet looked to the player like the app was broken rather than like the link
// was wrong. This screen says which of the two it is.
//
// It guesses nothing and redirects nowhere. Somebody who lands here followed a
// link that does not resolve; moving them on automatically would hide that and
// they would never know why the thing they tapped did not work. It offers the
// two destinations such a person actually wants — back into a game, or the
// website — and lets them choose.
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { track } from '@tasted/utils/analytics';
import FullscreenLayout from '../components/FullscreenLayout';
import { getWebAppUrl } from '../utils/webAppUrl';

export default function NotFound() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // A dead link used to leave no trace at all: the player saw a blank screen
  // and nobody found out. Record the path (no query string — an emailed link
  // can carry a token in it) so the next bad link shows up as a number.
  useEffect(() => {
    try {
      track('Page Not Found', { app: 'game', path: pathname });
    } catch {
      /* analytics must never be the reason this screen fails to render */
    }
  }, [pathname]);

  const eyebrowStyle = { color: '#C9A84C', letterSpacing: '3px' };
  const headingStyle = { fontFamily: "'Fraunces', Georgia, serif", color: '#0E2010' };
  const bodyColor = 'color-mix(in oklab, #0E2010 60%, transparent)';
  const buttonStyle = { background: '#0E2010', fontFamily: "'Outfit', system-ui, sans-serif" };

  return (
    <FullscreenLayout>
      <div
        className="flex-1 min-h-0 overflow-y-auto flex flex-col px-6 pt-12 pb-6 bg-[#F5E6C8]"
        data-testid="game-not-found"
      >
        <div className="max-w-sm mx-auto w-full flex flex-col gap-6 flex-1 justify-center text-center">
          <div className="text-5xl" aria-hidden>🔗</div>
          <p className="text-[10px] font-medium tracking-[3px] uppercase" style={eyebrowStyle}>
            Link not found
          </p>
          <h1
            className="font-bold leading-[1.08] text-[28px] tracking-[-0.3px] text-balance"
            style={headingStyle}
          >
            That link didn't lead anywhere.
          </h1>
          <p className="text-[14px] leading-relaxed" style={{ color: bodyColor }}>
            Nothing in the game sits at this address. Email apps sometimes break a link in half,
            and a link from an older message can stop working. Your game and your score are fine.
          </p>
          <p
            className="text-[12px] font-mono break-all"
            style={{ color: 'color-mix(in oklab, #0E2010 40%, transparent)' }}
          >
            {pathname}
          </p>

          <div className="flex flex-col gap-3">
            <button
              type="button"
              onClick={() => navigate('/')}
              className="w-full rounded-xl py-3.5 text-[15px] font-semibold text-white transition-opacity"
              style={buttonStyle}
            >
              Join a game with a code
            </button>
            <a
              href={getWebAppUrl()}
              className="block w-full rounded-xl py-3.5 text-center text-[15px] font-semibold no-underline transition-opacity"
              style={{
                border: '1.5px solid color-mix(in oklab, #0E2010 25%, transparent)',
                color: '#0E2010',
                fontFamily: "'Outfit', system-ui, sans-serif",
              }}
            >
              Go to tasted.co.za
            </a>
          </div>
        </div>
      </div>
    </FullscreenLayout>
  );
}
